import { XIcon } from "lucide-react"
import type { ReactNode } from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

interface FilterChipProps {
  label: string
  /** O que está escolhido. Vazio significa filtro não aplicado. */
  value?: string
  onClear: () => void
  /**
   * Se o ✕ aparece. O chip de data está sempre preenchido — mostra o padrão da tela — mas
   * limpá-lo não faria nada enquanto o recorte for o padrão.
   */
  canClear?: boolean
  children: ReactNode
}

/** Quanto do valor cabe no chip antes de virar parede de texto. */
const CHIP_VALUE_MAX = 22

/**
 * Um filtro da barra: fechado é um botão, aberto é um popover com o controle dele.
 *
 * <p>Aplicado, o botão passa a mostrar o valor e ganha o ✕ que o desliga — assim a barra
 * inteira responde "o que está filtrando?" sem obrigar a abrir nada. Era esse o custo de deixar
 * os campos abertos na tela: para caber, ou a página crescia, ou o filtro sumia.</p>
 */
export const FilterChip = ({
  label,
  value,
  onClear,
  canClear = true,
  children,
}: FilterChipProps) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const isApplied = Boolean(value)
  const shortValue =
    value && value.length > CHIP_VALUE_MAX
      ? `${value.slice(0, CHIP_VALUE_MAX)}…`
      : value

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <div
        className={cn(
          "inline-flex items-center rounded-md border text-sm transition-colors",
          isApplied
            ? "btn-whitelabel border-transparent"
            : "bg-background hover:bg-muted"
        )}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            title={isApplied ? `${label}: ${value}` : label}
            className="whitespace-nowrap rounded-l-md px-2.5 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-foreground"
          >
            {isApplied ? `${label}: ${shortValue}` : label}
          </button>
        </PopoverTrigger>
        {isApplied && canClear && (
          <button
            type="button"
            onClick={onClear}
            aria-label={`${t("orders.filters.removeFilter")}: ${label}`}
            className="rounded-r-md py-1 pr-1.5 opacity-80 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-foreground"
          >
            <XIcon size={14} />
          </button>
        )}
      </div>
      <PopoverContent
        align="start"
        className="w-auto min-w-64 max-w-[95vw] space-y-3 p-3"
      >
        {children}
        <div className="flex items-center justify-between gap-2 border-t pt-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClear}
          >
            {t("orders.filters.chipClear")}
          </Button>
          <Button
            type="button"
            size="sm"
            className="btn-whitelabel"
            onClick={() => setIsOpen(false)}
          >
            {t("orders.filters.chipApply")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
